import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import type { WorkoutHistoryItem } from '../types';
import { fetchWorkoutsRows } from '../services/fetchWorkoutsRows';
import { deleteWorkout as deleteWorkoutRemote } from '../services/historyService';

export type { WorkoutHistoryItem };

const DELETE_ERROR_TOAST = 'Nie udało się usunąć treningu. Spróbuj ponownie';

/**
 * Subscribes to auth-backed workout history: fetches rows when `user` is set,
 * clears when logged out, and exposes `fetchWorkouts` for manual refresh.
 */
export function useWorkoutHistory(user: unknown, session: unknown) {
  const [workouts, setWorkouts] = useState<WorkoutHistoryItem[]>([]);
  const [fetchingWorkouts, setFetchingWorkouts] = useState(false);
  const [deletingWorkoutId, setDeletingWorkoutId] = useState<string | null>(null);

  const fetchWorkouts = useCallback(async () => {
    const u = user as { id?: string } | null | undefined;
    if (!u?.id) {
      setWorkouts([]);
      return;
    }

    setFetchingWorkouts(true);
    try {
      const rows = await fetchWorkoutsRows(u.id);
      setWorkouts(rows);
    } finally {
      setFetchingWorkouts(false);
    }
  }, [user]);

  useEffect(() => {
    void fetchWorkouts();
  }, [session, user, fetchWorkouts]);

  const handleDeleteWorkout = useCallback(
    async (id: string) => {
      const u = user as { id?: string } | null | undefined;
      if (!u?.id) {
        toast.error(DELETE_ERROR_TOAST);
        return;
      }

      const previousWorkouts = workouts;
      setWorkouts((prev) => prev.filter((w) => w.id !== id));
      setDeletingWorkoutId(id);

      const { error } = await deleteWorkoutRemote(id, u.id);
      setDeletingWorkoutId(null);

      if (error) {
        console.error('[useWorkoutHistory] delete failed:', error.message, error);
        setWorkouts(previousWorkouts);
        toast.error(DELETE_ERROR_TOAST);
      }
    },
    [user, workouts],
  );

  return {
    workouts,
    fetchingWorkouts,
    fetchWorkouts,
    handleDeleteWorkout,
    deletingWorkoutId,
  };
}
